import { useState } from 'react';
import { integrationApi, templatesApi } from '../../api/services';
import type { IntegrationOperationStatusDto, SaveTemplateDto, TemplateDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Form, Input, Loadable, Modal, PageHeader, Select, Table, Tabs, Toolbar, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { OperationAssignments } from './OperationAssignments';
import { integrationTemplateKeys, isIntegrationOperation } from './integrationScope';


export function TemplatesPage() {
  const [tab, setTab] = useState<'templates' | 'operations'>('templates');
  return (
    <>
      <PageHeader title="Plantillas contables" subtitle="Las cuentas de los asientos automáticos salen siempre de aquí; la API no tiene cuentas escritas en código." />
      <PageHelp page="templates" />
      <Tabs tabs={[{ key: 'templates', label: 'Plantillas' }, { key: 'operations', label: 'Plantillas por operación' }]} active={tab} onChange={setTab} />
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'operations' && <OperationAssignments />}
    </>
  );
}

function TemplatesTab() {
  const modules = useAsync(() => templatesApi.modules(), []);
  const [moduleCode, setModuleCode] = useState('');
  const [text, setText] = useState('');
  const list = useAsync(() => templatesApi.search({ moduleCode: moduleCode || undefined, text: text.trim() || undefined, pageSize: 200 }), [moduleCode, text]);
  const allOps = useAsync(() => integrationApi.operations().catch(() => []), []);
  const labels = conceptLabels(allOps.data);
  // Las plantillas de facturación, pagos y cajas se editan en Integración: aquí no se muestran.
  const hidden = integrationTemplateKeys(allOps.data);
  const [editing, setEditing] = useState<TemplateDto | 'new' | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <Select value={moduleCode} onChange={setModuleCode} placeholder="Todos los módulos" options={(modules.data ?? []).map((m) => ({ value: m.code, label: `${m.code} · ${m.description}` }))} />
        <Input value={text} onChange={setText} placeholder="Buscar" />
        <Button kind="primary" onClick={() => setEditing('new')}>Nueva plantilla</Button>
      </Toolbar>
      <p className="acc-muted">Las plantillas de facturación, pagos y cajas se configuran en Configuración → Integración.</p>
      <Loadable state={list}>
        {(r) => (
          <Table rows={r.items.filter((t) => !hidden.has(`${t.moduleCode}-${t.code}`))} rowKey={(t) => `${t.moduleCode}-${t.code}`} onRowClick={(t) => templatesApi.get(t.moduleCode, t.code).then(setEditing)} columns={[
            { key: 'm', title: 'Módulo', render: (t) => t.moduleCode },
            { key: 'c', title: 'Código', render: (t) => t.code },
            { key: 'd', title: 'Descripción', render: (t) => t.description },
            { key: 'k', title: 'Conceptos', render: (t) => (t.conceptMapping ? (t.conceptMappingValid ? <small>{describeMapping(t.conceptMapping, labels)}</small> : <Badge tone="bad">mapeo inválido</Badge>) : <span className="acc-muted">—</span>) },
            { key: 'e', title: 'Estado', render: (t) => <Badge tone={t.status === 'A' ? 'ok' : 'neutral'}>{t.status === 'A' ? 'Activa' : 'Inactiva'}</Badge> },
            { key: 'x', title: '', render: (t) => <span onClick={(e) => e.stopPropagation()}><Button kind="link" busy={busy} onClick={() => run(() => templatesApi.setStatus(t.moduleCode, t.code, t.status !== 'A'), { success: 'Estado actualizado.' }).then(list.reload)}>{t.status === 'A' ? 'Inactivar' : 'Activar'}</Button></span> },
          ]} />
        )}
      </Loadable>
      {editing && <TemplateForm template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function TemplateForm({ template, onClose, onSaved }: { template: TemplateDto | null; onClose: () => void; onSaved: () => void }) {
  const modules = useAsync(() => templatesApi.modules(), []);
  const [v, setV] = useState<SaveTemplateDto>({
    moduleCode: template?.moduleCode ?? '', transactionCode: template?.transactionCode ?? 0, subTransactionCode: template?.subTransactionCode ?? '0',
    description: template?.description ?? '', processType: template?.processType ?? null, conceptMapping: template?.conceptMapping ?? '', templateTypeId: template?.templateTypeId ?? null,
    lines: template?.lines.map((l) => ({ accountNumber: l.accountNumber, debitCredit: l.debitCredit })) ?? [{ accountNumber: '', debitCredit: 'D' }, { accountNumber: '', debitCredit: 'C' }],
  });
  // Transacción: la asigna el sistema (0 = crear una propia al guardar; al modificar se conserva la que tiene).
  // Campo (concepto) de cada línea; el mapeo de la plantilla se arma con esto al guardar.
  const [lineConcepts, setLineConcepts] = useState<(string | undefined)[]>(() => {
    const m = conceptsByLine(template?.conceptMapping);
    return (template?.lines ?? [{}, {}]).map((_, i) => m[i + 1]);
  });
  const ops = useOperationConcepts();
  const labels = conceptLabels(ops.data);
  const conceptOptions = (() => {
    const seen = new Map<string, string>();
    for (const o of (ops.data ?? []).filter((x) => x.moduleCode === v.moduleCode))
      for (const c of o.conceptList) if (!seen.has(c)) seen.set(c, `${labels[c] ?? c} · ${o.name}`);
    for (const c of lineConcepts) if (c && !seen.has(c)) seen.set(c, labels[c] ?? c);
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  })();
  const { run, busy, fieldErrors } = useAction();
  const setLine = (i: number, patch: Partial<SaveTemplateDto['lines'][number]>) => setV((x) => ({ ...x, lines: x.lines.map((l, k) => (k === i ? { ...l, ...patch } : l)) }));
  const save = () => run(async () => {
    const kept = v.lines.map((l, i) => ({ l, c: lineConcepts[i] })).filter((x) => x.l.accountNumber.trim());
    const dto = { ...v, conceptMapping: buildMapping(kept.map((x) => x.c)), lines: kept.map((x) => x.l) };
    if (template) await templatesApi.update(template.moduleCode, template.code, dto); else await templatesApi.create(dto);
    onSaved();
  }, { success: 'Plantilla guardada.' });

  return (
    <Modal title={template ? `Plantilla ${template.moduleCode}-${template.code}` : 'Nueva plantilla'} onClose={onClose} size="lg">
      <Form onSubmit={save}>
        <div className="acc-grid-form">
          <Field label="Módulo"><Select value={v.moduleCode} disabled={!!template} placeholder="Seleccione" onChange={(x) => setV({ ...v, moduleCode: x })} options={(modules.data ?? []).map((m) => ({ value: m.code, label: `${m.code} · ${m.description}` }))} /></Field>
          <Field label="Descripción" error={fieldErrors.Description}><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} required maxLength={200} /></Field>
        </div>
        <Alert>En cada línea elija el campo cuyo monto va a esa cuenta. Por ejemplo, en una factura: el Total al débito (cuentas por cobrar), el Subtotal al crédito (ingresos) y el Impuesto de ventas al crédito (impuesto por pagar). Los campos que ninguna línea usa se ignoran; el asiento debe cuadrar.</Alert>
        <Card title="Líneas">
          <Table rows={v.lines.map((l, i) => ({ ...l, i }))} rowKey={(l) => l.i} columns={[
            { key: 'n', title: 'Línea', render: (l) => l.i + 1 },
            { key: 'a', title: 'Cuenta', render: (l) => <AccountPicker value={l.accountNumber} onChange={(x) => setLine(l.i, { accountNumber: x })} /> },
            { key: 'd', title: 'D/C', render: (l) => <Select value={l.debitCredit} onChange={(x) => setLine(l.i, { debitCredit: x })} options={[{ value: 'D', label: 'Débito' }, { value: 'C', label: 'Crédito' }]} /> },
            { key: 'c', title: 'Campo (concepto)', render: (l) => <Select value={lineConcepts[l.i] ?? ''} placeholder={v.moduleCode ? '(ninguno)' : 'Elija el módulo'} onChange={(x) => setLineConcepts((cs) => { const n = [...cs]; n[l.i] = x || undefined; return n; })} options={conceptOptions} /> },
            { key: 'x', title: '', render: (l) => <Button kind="link" disabled={v.lines.length <= 2} onClick={() => { setV({ ...v, lines: v.lines.filter((_, k) => k !== l.i) }); setLineConcepts((cs) => cs.filter((_, k) => k !== l.i)); }}>Quitar</Button> },
          ]} />
          <Button onClick={() => { setV({ ...v, lines: [...v.lines, { accountNumber: '', debitCredit: 'D' }] }); setLineConcepts((cs) => [...cs, undefined]); }}>Agregar línea</Button>
        </Card>
        {template && !template.conceptMappingValid && template.conceptMapping && <Alert kind="error">El mapeo actual referencia líneas inexistentes o tiene formato inválido.</Alert>}
        <div className="acc-actions">
          {template && <Button kind="danger" busy={busy} onClick={() => run(async () => { await templatesApi.remove(template.moduleCode, template.code); onSaved(); }, { confirm: 'Eliminar la plantilla? Si ya fue usada, inactívela en su lugar.', danger: true, success: 'Plantilla eliminada.' })}>Eliminar</Button>}
          <Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button>
        </div>
      </Form>
    </Modal>
  );
}

/** Operaciones base con los conceptos (campos) que la API envía en cada una; vienen de la API, no de una lista fija. */
function useOperationConcepts() {
  // Las operaciones de integración (facturación, pagos, cajas) tienen sus plantillas en Integración.
  return useAsync<IntegrationOperationStatusDto[]>(() => integrationApi.operations().then((ops) => ops.filter((o) => !o.paymentType && !isIntegrationOperation(o))), []);
}

/** "IMPUESTOVENTASLOCAL=1;TOTALLOCAL=2,3" → concepto de cada línea (1..n). */
function conceptsByLine(mapping: string | null | undefined): Record<number, string> {
  const out: Record<number, string> = {};
  for (const part of (mapping ?? '').split(';')) {
    const [concept, lines] = part.split('=');
    if (!concept || !lines) continue;
    for (const n of lines.split(',')) {
      const k = Number(n.trim());
      if (k > 0) out[k] = concept.trim().toUpperCase();
    }
  }
  return out;
}

/** Nombre visible de cada campo, tomado de las operaciones (nunca se muestra el nombre técnico). */
function conceptLabels(ops: IntegrationOperationStatusDto[] | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const o of ops ?? []) for (const [k, v] of Object.entries(o.conceptLabels ?? {})) out[k.toUpperCase()] = v;
  return out;
}

/** "IMPUESTOVENTASLOCAL=1;TOTALLOCAL=2" → "Línea 1: Impuesto de ventas · Línea 2: Total". */
function describeMapping(mapping: string, labels: Record<string, string>): string {
  const byLine = conceptsByLine(mapping);
  return Object.keys(byLine).map(Number).sort((a, b) => a - b)
    .map((n) => `Línea ${n}: ${labels[byLine[n]] ?? byLine[n].charAt(0) + byLine[n].slice(1).toLowerCase().replace(/_/g, ' ')}`).join(' · ');
}

/** Concepto por línea → mapeo de la plantilla, en el orden de las líneas. */
function buildMapping(concepts: (string | undefined)[]): string | null {
  const byConcept = new Map<string, number[]>();
  concepts.forEach((c, i) => {
    if (!c) return;
    byConcept.set(c, [...(byConcept.get(c) ?? []), i + 1]);
  });
  const parts = [...byConcept.entries()].map(([c, lines]) => `${c}=${lines.join(',')}`);
  return parts.length ? parts.join(';') : null;
}
